import { emptyFragment, UserMessageIcon } from '@execonline-inc/execonline-ui';
import { whenGt } from '@execonline-inc/numbers';
import { Maybe } from 'maybeasy';
import { observer } from 'mobx-react';
import * as React from 'react';
import ProgramStore from '../../../../../ProgramStore';
import { ActiveProgram, CoachingProductDetails } from '../../../../../ProgramStore/Types';
import { T } from '../../../../../Translations';
import UpcomingSessionsRefresh from '../../../../../UpcomingSessionsRefresh';
import {
  ProgramCard,
  ProgramCardActions,
  ProgramCardBody,
  ProgramCardFooter,
  ProgramCardHeader,
  ProgramCardTitle,
} from '../../../../Dashboard/ProgramCard';
import ProgramCardTeam from '../../../../Dashboard/ProgramCard/ProgramCardTeam';
import UpcomingSessions from '../../../../Dashboard/ProgramCard/UpcomingSessions';
import ModalStore from '../../../../Modal/Store';
import ScheduleSessionButton from '../ScheduleSessionButton';
import CoachingSummary from './CoachingSummary';
import CoachViewProfileModal from './CoachViewProfileModal';

interface Props {
  activeProgram: ActiveProgram;
  programStore: ProgramStore;
  coachingProductDetails: CoachingProductDetails;
  modalStore: ModalStore;
}

const whenUserHasActiveLicense = (programStore: ProgramStore): Maybe<number> =>
  programStore.remainingDays.andThen(whenGt(0));

const CompletedOrientation: React.FC<Props> = ({
  activeProgram,
  programStore,
  coachingProductDetails,
  modalStore,
}) => (
  <ProgramCard data-test-active-program={true}>
    <ProgramCardHeader
      links={programStore.programLinks}
      programInstanceKind={activeProgram.programInstanceKind}
      label={
        <span className="inline-flex items-center gap-1.5 text-sm font-medium text-primary">
          <UserMessageIcon size="sm" color="inherit" />
          <T kind="1:1 Coaching" />
        </span>
      }
    />
    {programStore.title.map((title) => <ProgramCardTitle title={title} />).getOrElse(emptyFragment)}
    <ProgramCardBody>
      <CoachingSummary
        programStore={programStore}
        coachingProductDetails={coachingProductDetails}
        modalStore={modalStore}
      />
      {whenUserHasActiveLicense(programStore)
        .map(() => (
          <ProgramCardActions>
            <ScheduleSessionButton programStore={programStore} />
          </ProgramCardActions>
        ))
        .getOrElse(emptyFragment)}
    </ProgramCardBody>
    <UpcomingSessionsRefresh programStore={programStore} />
    <ProgramCardFooter>
      <ProgramCardTeam programId={activeProgram.id} />
      {/* The sessions payload holds at most 3; the full list is in the events panel on the right. */}
      <UpcomingSessions
        label="Upcoming coaching sessions"
        sessions={coachingProductDetails.sessions.map(({ payload }) => ({
          id: payload.id,
          startAt: payload.date,
        }))}
        moreCount={Math.max(
          coachingProductDetails.upcomingSessionsCount - coachingProductDetails.sessions.length,
          0,
        )}
      />
    </ProgramCardFooter>
    {coachingProductDetails.selectedCoach
      .map((coachProfile) => (
        <CoachViewProfileModal coachProfile={coachProfile} modalStore={modalStore} />
      ))
      .getOrElse(emptyFragment)}
  </ProgramCard>
);

export default observer(CompletedOrientation);
